import { ChangeEvent } from '@disy/lib/event-util';
import type { ValidationMessageType, ValidationMessages } from '@disy/ui/form-validation-messages';
import { validationMessage } from '@disy/ui/form-validation-messages';

export type FormElementOptions<T> = {
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  validationMessages?: ValidationMessages;
} & (
  | {
      defaultValue: T;
    }
  | {
      defaultValue?: T;
      valueToString?: (value: T) => string;
      valueFromString: (value: string) => T;
    }
);

/**
 * ## Motivation and General Information
 * This abstract class exposes an API to create [form-associated custom elements](https://html.spec.whatwg.org/multipage/custom-elements.html#form-associated-custom-elements).
 * Why should you extend this class?! Foremost, it auto-wires your component with the corresponding form and the label;
 * this enables the component to participate in the form submission.
 * Furthermore, it implements accessibility best practices, so that you don't have to bother.
 * `FormElement` instantiates the **essential ARIA attributes**, helping the developer and reducing redundant code.
 * Under the hood, `FormElement` uses the [Element Internals API](https://developer.mozilla.org/en-US/docs/Web/API/ElementInternals).
 *
 *
 * ## How to Implement a Subclass
 *
 * ### Special Notes
 *
 * #### FormGroup
 * Worth a mention is that subclasses of `FormElement` are compatible with {@link #FormGroup}.
 *
 * #### Value
 * Use always the super `value` and `defaultValue` to ensure a **single source of truth** and prevent inconsistencies.
 *
 * In contrast to the `value` setter, the `setValue` method dispatches a change event after setting the value.
 * Use it when changing the value after a user interaction, use the setter otherwise.
 *
 * Calling the `reset` method, sets the value to the default value.
 *
 * #### Options priority
 * One can set properties (such as required or disabled) in two different ways:
 * (1) content attribute, (2) via constructor parameters; while content attributes win over constructor parameters.
 * If none of these are present, we use fallback values.
 *
 * #### Validation Messages
 * `FormElement` allows customizing validation messages. Therefore, it considers messages defined via data-* attributes,
 * passed via constructor options `validationMessages`, or passed via the {@link #setCustomValidity} method.
 *
 * The validation messages cascade as follows:
 * messages passed via the dataset property or {@link #setCustomValidity} >
 *  messages passed via constructor options >
 *    default form-validation-messages.properties
 *
 *
 * ### Approach
 * - Extend from abstract class `FormElement`.
 * - Pass all required properties to super class.
 * - For `connectedCallback`, and `disconnectedCallback` always call corresponding super callback first.
 * - If the component is complex and requires focussing specific HTMLElement, overwrite the {@link `#focus`} method.
 * - Override {@link #createValidity} for more specific violation checking.
 *   Call super method to check for value missing violations.
 * - Override the `apply*` methods to customize how corresponding changes are applied.
 *   (Simply overriding the corresponding setter would accidentally kill the getter.)
 * - Override `sanitizeValue` to sanitize or constrain the value or default value.
 * - If you need logic to convert between string values and actual values, provide `valueFromString` and optionally `valueToString`.
 * - If you require specific behavior for resetting logic, override the `formResetCallback` callback.
 * However, don't forget to call super to corresponding super callback first.
 * - ElementInternals API provides [further lifecycle callbacks](https://html.spec.whatwg.org/multipage/custom-elements.html#concept-custom-element-definition-lifecycle-callbacks)
 * (`formStateRestoreCallback` and `formAssociatedCallback`).
 * - If you implement a form element that is not based on built-in form elements (for example using a quill editor), be aware that you need to fire a 'change' event when the content changes. Also you need to call this#validate on content change, unless you force inline validation, (see {@link FormValidation#forceInlineValidation}).
 *
 * ## Further Information
 * _Note: These examples focus on how to use the ElementInternals API. They are listed here just as a hint
 * and are not required to be able subclassing `FormElement`._
 * - [Example from HTML Spec](https://html.spec.whatwg.org/multipage/custom-elements.html#custom-elements-face-example)
 * - [How-To and extended example](https://web.dev/more-capable-form-controls/)
 */
export abstract class FormElement<T = unknown> extends HTMLElement {
  /*
   * Convention from ElementInternals API.
   * Returning `true` makes the custom element a form-associated custom element.
   */
  static get formAssociated() {
    return true;
  }

  static get observedAttributes(): readonly string[] {
    return ['readonly'];
  }

  readonly #internals = this.attachInternals();

  #value?: T;
  #defaultValue;

  readonly #validationMessages;

  readonly #initialId;
  readonly #initialName;

  readonly #initiallyRequired;
  readonly #initiallyDisabled;

  readonly #valueToString;
  readonly #valueFromString;

  /**
   * @param options - Options
   * @param options.id - The ID of this form element, e.g. for use with the "for" element of its `<label>`
   * @param options.name - The name of this form element.
   *  Is used to identify the control when submitting the form.
   * @param options.defaultValue - The default value is used either for initializing the control
   *  or as initial value when resetting form or control.
   * @param options.disabled - Indicates whether the control is initially disabled.
   * If a form element is disabled, it cannot be interacted with, and its value is not submitted with the form.
   * @param options.required - Indicates whether the custom form control is initially required.
   * @param options.validationMessages - Custom validation messages.
   * @param options.valueToString - Serialize a value (e.g. to the "value" attribute)
   * @param options.valueFromString - Deserialize a value (e.g. from the "value" attribute)
   */
  protected constructor(options: FormElementOptions<T>) {
    super();

    this.#defaultValue = options.defaultValue;

    if ('valueFromString' in options) {
      this.#valueToString = options.valueToString ?? String;
      this.#valueFromString = options.valueFromString;
      if (options.defaultValue && !this.hasAttribute('value')) {
        this.setAttribute('value', this.#valueToString(options.defaultValue));
      }
    }
    this.#validationMessages = options.validationMessages ?? {};

    this.#initialId = options.id;
    this.#initialName = options.name;

    this.#initiallyDisabled = options.disabled;
    this.#initiallyRequired = options.required;
  }

  protected connectedCallback() {
    this.classList.add('form-group-control');

    if (this.#initialId != null) {
      this.id = this.#initialId;
    }
    if (this.#initialName != null) {
      this.name = this.#initialName;
    }
    if (this.#initiallyDisabled != null) {
      this.disabled = this.#initiallyDisabled;
    } else {
      this.applyDisabled(this.disabled);
    }
    if (this.#initiallyRequired != null) {
      this.required = this.#initiallyRequired;
    }

    this.applyValue(this.value);
    this.applyReadOnly(this.readOnly);
    this.validate();
  }

  protected disconnectedCallback() {
    // Noop (Might be used in the future.)
  }

  get form() {
    return this.#internals.form;
  }

  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(value: string) {
    if (value) {
      this.setAttribute('name', value);
    } else {
      this.removeAttribute('name');
    }
  }

  get value(): T {
    return this.#value != null ? this.sanitizeValue(this.#value) : this.defaultValue;
  }

  set value(value: T | undefined) {
    this.#value = value;
    this.validate();
    this.applyValue(this.value);
  }

  /**
   * Override this method if you want to apply some rules to the value before applying it,
   * for example, for numbers, rounding or clamping.
   */
  protected sanitizeValue(value: T) {
    return value;
  }

  /**
   * Override this method if you need to react to value changes.
   */
  protected applyValue(value: T) {
    // noop
  }

  /**
   * Sets the value and dispatches a change event.
   * Call this method when the value changes due to user interaction.
   *
   * @param value - The new value
   */
  protected setValue(value: T | undefined) {
    this.value = value;
    this.dispatchEvent(new ChangeEvent());
  }

  get defaultValue() {
    const valueFromString = this.#valueFromString;
    return this.sanitizeValue(
      // #defaultValue must be defined if #valueFromString is not (see constructor options)
      valueFromString ? valueFromString(this.getAttribute('value') ?? '') : this.#defaultValue!,
    );
  }

  set defaultValue(value: T) {
    const valueToString = this.#valueToString;
    if (valueToString) {
      this.setAttribute('value', valueToString(value));
    } else {
      this.#defaultValue = value;
    }
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }

  /*
   * Changing the disabled state of this control will trigger the `formDisabledCallback`.
   * This callback contains the actual disabling logic.
   */
  set disabled(value) {
    this.toggleAttribute('disabled', value);
  }

  get readOnly() {
    return this.hasAttribute('readonly');
  }

  set readOnly(value) {
    this.toggleAttribute('readonly', value);
  }

  /**
   * Override this method if you need to react to changes of the readOnly property.
   */
  protected applyReadOnly(value: boolean) {
    // noop
  }

  attributeChangedCallback(name: string) {
    switch (name) {
      case 'readonly': {
        const readOnly = this.readOnly;
        this.ariaReadOnly = String(readOnly);
        this.validate();
        this.applyReadOnly(readOnly);
        break;
      }
    }
  }

  /**
   * This callback is called when either
   *   - the disabled attribute of this control changed or
   *   - the disabled state on a `<fieldset>` changed that's an ancestor of this control.
   *
   * @param disabled - the disabled state of this control
   */
  protected formDisabledCallback(disabled: boolean) {
    this.ariaDisabled = String(disabled);
    this.validate();
    this.applyDisabled(disabled);
  }

  /**
   * Override this method if you need to react to changes of the disabled property.
   */
  protected applyDisabled(value: boolean) {
    // noop
  }

  get required() {
    return this.hasAttribute('required');
  }

  set required(value) {
    this.toggleAttribute('required', value);
    this.ariaRequired = String(value);
    this.validate();
  }

  get min() {
    if (this.hasAttribute('min')) {
      return Number.parseFloat(this.getAttribute('min')!);
    }
    return Number.NaN;
  }

  get max() {
    if (this.hasAttribute('max')) {
      return Number.parseFloat(this.getAttribute('max')!);
    }
    return Number.NaN;
  }

  get validity() {
    return this.#internals.validity;
  }

  get validationMessage() {
    return this.#internals.validationMessage;
  }

  #resolveValidationMessage(validity: Omit<ValidityStateFlags, 'customError'>) {
    const defaultMessage = this.dataset.validationMessage ?? '';
    if (validity.valueMissing) {
      return this.#resolveMessage('valueMissing');
    } else if (validity.badInput) {
      // Defaults to default message because no specific message is defined for badInput
      return this.dataset.badInputMessage || defaultMessage;
    } else if (validity.rangeOverflow || validity.rangeUnderflow) {
      const min = this.min;
      const max = this.max;
      const useOutOfRangeMessage =
        this.dataset.outOfRangeMessage != null ||
        !(this.dataset.rangeOverflowMessage || this.dataset.rangeUnderflowMessage);
      if (min != null && max != null && useOutOfRangeMessage) {
        return this.#resolveMessage('outOfRange', { min, max });
      } else if (validity.rangeUnderflow) {
        return this.#resolveMessage('rangeUnderflow', { limit: min });
      } else {
        return this.#resolveMessage('rangeOverflow', { limit: max });
      }
    } else if (validity.tooLong) {
      return this.#resolveMessage('tooLong');
    } else if (validity.tooShort) {
      return this.#resolveMessage('tooShort');
    } else if (validity.patternMismatch) {
      return this.#resolveMessage('patternMismatch');
    }
    return defaultMessage;
  }

  #resolveMessage(flag: ValidationMessageType, options?: Record<string, unknown>) {
    return this.dataset[`${flag}Message`] ?? this.#validationMessages[flag] ?? validationMessage(flag, options);
  }

  get willValidate() {
    return this.#internals.willValidate;
  }

  /**
   * Call this when the value or the validation constraints for the form element changed.
   */
  protected validate() {
    if (!this.willValidate) {
      this.#internals.setValidity({});
      return;
    }
    const { customError, ...tempValidity } = this.createValidity();
    const validity = { ...tempValidity, customError: Boolean(customError) };
    const message = customError || this.#resolveValidationMessage(validity);
    this.#internals.setValidity(validity, message);
  }

  /**
   * Override in subclass to implement more specific validation of the component.
   * By default, this method checks only for value missing violation
   * and returns a corresponding validation state object.
   * If no violations are found this method must return an empty object (`{}`).
   * For custom validation errors, set the customError field to the custom message to show,
   * instead of a boolean.
   */
  protected createValidity(): Omit<ValidityStateFlags, 'customError'> & { customError?: string } {
    const value = this.value;
    const validity: Omit<ValidityStateFlags, 'customError'> & { customError?: string } = {
      valueMissing: this.required && (value == null || value === '' || (Array.isArray(value) && value.length === 0)),
    };
    const min = this.min;
    const max = this.max;
    if (!Number.isNaN(min)) {
      validity.rangeUnderflow = Number(value) < min;
    }
    if (!Number.isNaN(max)) {
      validity.rangeOverflow = Number(value) > max;
    }
    return validity;
  }

  setCustomValidity(message: string) {
    const state = { ...this.validity, customError: Boolean(message) };
    message = state.customError ? message : this.#resolveValidationMessage(state);
    this.#internals.setValidity(state, message);
  }

  checkValidity() {
    return this.#internals.checkValidity();
  }
  reportValidity() {
    return this.#internals.reportValidity();
  }

  reset() {
    this.formResetCallback();
  }

  /**
   * This callback is called after the form element or its form is reset.
   * Subclasses might override this callback for more control over how and to which value
   * the control needs to be reset. By default, this callback resets the value to the `defaultValue`.
   */
  protected formResetCallback() {
    this.value = undefined;
  }

  /**
   * Override this method to customize the focus behavior of your form element.
   */
  override focus() {
    super.focus();
  }
}
